import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import withWidth from '@material-ui/core/withWidth';

import { Field, change, getFormValues } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import FormControl from '@material-ui/core/FormControl';

import TabNavigation from 'components/common/TabNavigation';
import Layout from 'components/common/Layout';
import Button from 'components/common/Button';
import Progress from 'components/common/Progress';
import Filter from 'components/common/Filter';
import UsersTable from 'components/settings/UsersTable';
import RadioButtonField from 'components/fields/RadioButtonField';
import RadioGroupField from 'components/fields/RadioGroupField';
import AutosuggestField from 'components/fields/AutosuggestField';

import {
  fetchUsers,
  clearUsers,
} from 'redux/modules/users';
import {
  requestFetchRoles,
  requestSearchRoles,
} from 'redux/modules/roles';
import { accountLoginFail } from 'redux/modules/account';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';

import { appTitle, titleSeparator } from 'config/helmetSettings';

const styles = () => ({
  filter: {
    zIndex: 21,
    '&:before': {
      bottom: -16,
      top: 0,
    },
  },
});

class Users extends Component {
  constructor(props) {
    super(props);

    this.state = {
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
      limit: 10,
      offset: 0,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);
    this.onTableScroll = this.onTableScroll.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    this.props.actions.clearUsers();
  }

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filters, width } = this.props;
    const { limit, offset } = this.state;
    const filtersChanged = nextProps.filters['Full Name'] !== filters['Full Name']
      || nextProps.filters.Active !== filters.Active
      || nextProps.filters.Role !== filters.Role;
    let newOffset = offset;
    if (filtersChanged) {
      newOffset = 0;
    }
    if (newOffset !== offset) {
      this.setState({ offset: newOffset });
    }

    if (nextProps.width !== width) {
      this.setState({
        mobileFilterExpanded: ['xs', 'sm'].indexOf(nextProps.width) === -1,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters, users, count, loading } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['Full Name'],
        immediateFilterKeys: ['Active', 'Role'],
      },
    );

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (limitChanged || offsetChanged || filtersChanged) {
      this.onRequest();
    } else if (users.length < count
      && this.scrollRef
      && this.scrollRef.scrollTop === 0
      && loading === 0) {
      this.onTableScroll({ target: this.scrollRef });
    }
  }

  onRequest() {
    const { actions, filters } = this.props;
    const { limit, offset } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map(key => ({
        name: key,
        operation: key === 'Full Name' ? 'startswith' : 'eq',
        val: filters[key],
        ...key === 'Full Name' ? { case_sensitivity: false } : {},
      }))
      : [];
    actions.fetchUsers({
      limit,
      offset,
      filters: [
        ...requestFilters,
        ...!filters.Role ? [{
          name: 'Role',
          operation: 'ne',
          val: 'System',
        }] : [],
      ],
      triggerLoading: true,
      push: offset > 0,
    });
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  onTableScroll(event) {
    const { offset, limit } = this.state;

    if ((event.target.offsetHeight + event.target.scrollTop) / event.target.scrollHeight > 0.9) {
      if ((this.props.users.length - limit) >= offset) {
        this.setState({
          offset: offset + limit,
        });
      }
    }
  }

  render() {
    const { users, count, loading, actions, classes, filters } = this.props;
    const { mobileFilterExpanded } = this.state;

    return (
      <Layout variant="settings">
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Users
          </title>
        </Helmet>
        <TabNavigation
          items={[
            {
              text: 'Users',
              href: '/settings/users',
            },
            {
              text: 'Roles and Permissions',
              href: '/settings/roles',
            },
          ]}
        />
        <Filter
          className={classes.filter}
          form="usersFilter"
          primaryButtonText="Add New"
          primaryButtonHref="/settings/users/create"
          placeholder="Search by User Name"
          searchWidth={5}
          onMobileFilterBottonClick={this.onMobileFilterBottonClick}
          mobileFilterExpanded={mobileFilterExpanded}
          filters={filters}
          showTableCount
          count={count}
          loading={loading}
          items={users}
          secondaryFilter={(
            <Fragment>
              <Grid item xs={6} md={2}>
                <Field
                  id="Role"
                  name="Role"
                  component={AutosuggestField}
                  placeholder="User Role"
                  fullWidth
                  renderSuggestionContent={suggestion => suggestion.Name}
                  renderInputContent={suggestion => suggestion.Name}
                  listWidth="auto"
                  onSuggestionSelected={suggestion => suggestion.Name}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      ? requestSearchRoles({
                        filters: [
                          ...value.length > 0
                            ? [{
                              name: 'Name',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            }] : [],
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchRoles({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                />
              </Grid>
              <Grid item>
                <Paper>
                  <FormControl role="group">
                    <Field
                      name="Active"
                      component={RadioGroupField}
                    >
                      {['All', 'Active', 'Inactive'].map(item => (
                        <RadioButtonField
                          key={item}
                          value={item}
                          label={item}
                          color="primary"
                        />
                      ))}
                    </Field>
                  </FormControl>
                </Paper>
              </Grid>
            </Fragment>
          )}
          initialValues={{ Active: 'Active' }}
          beforeTable
        />
        {loading > 0 &&
          <Progress fixed />
        }
        <UsersTable
          items={users}
          count={count}
          loading={loading}
          mobileFilterExpanded={mobileFilterExpanded}
          onScroll={this.onTableScroll}
          scrollRef={(scrollRef) => { this.scrollRef = scrollRef; }}
        />
      </Layout>
    );
  }
}

Users.propTypes = {
  users: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    'Full Name': PropTypes.string,
    Active: PropTypes.bool,
    Role: PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    fetchUsers: PropTypes.func.isRequired,
    clearUsers: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  width: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    filter: PropTypes.string.isRequired,
  }).isRequired,
};

Users.defaultProps = {};

const mapStateToProps = (store, ownProps) => {
  const usersFilterValues = getFormValues('usersFilter')(store);
  return {
    users: store.users.data,
    count: store.users.count,
    filters: {
      ...usersFilterValues
        && usersFilterValues.Filter
        ? { 'Full Name': usersFilterValues.Filter }
        : {},
      ...usersFilterValues
        && usersFilterValues.Active
        && usersFilterValues.Active !== 'All'
        ? { Active: usersFilterValues.Active === 'Active' }
        : {},
      ...usersFilterValues
        && usersFilterValues.Role
        ? { Role: usersFilterValues.Role }
        : {},
    },
    loading: store.users.loading,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchUsers,
    clearUsers,
    accountLoginFail,
    change,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(withWidth()(Users)),
);



// WEBPACK FOOTER //
// ./src/pages/settings/Users.js